export const crossSpeciesChatSpecies = [
  "Allosaurus",
  "Beipiaosaurus",
  "Carnotaurus",
  "Ceratosaurus",
  "Deinosuchus",
  "Diabloceratops",
  "Dilophosaurus",
  "Dryosaurus",
  "Gallimimus",
  "Herrerasaurus",
  "Hypsilophodon",
  "Kentrosaurus",
  "Maiasaura",
  "Omniraptor",
  "Pachycephalosaurus",
  "Pteranodon",
  "Stegosaurus",
  "Tenontosaurus",
  "Triceratops",
  "Troodon",
  "Tyrannosaurus"
] as const;

export type CrossSpeciesChatSpecies = (typeof crossSpeciesChatSpecies)[number];

export interface CrossSpeciesChatPair {
  speciesA: CrossSpeciesChatSpecies;
  speciesB: CrossSpeciesChatSpecies;
}

export interface CrossSpeciesChatConfig {
  enabled: boolean;
  maxRange: number;
  allowAllPairs: boolean;
  allowedPairs: CrossSpeciesChatPair[];
}

const speciesSet = new Set<string>(crossSpeciesChatSpecies);

export function isCrossSpeciesChatSpecies(value: string): value is CrossSpeciesChatSpecies {
  return speciesSet.has(value);
}

export function normalizeCrossSpeciesChatPairs(pairs: CrossSpeciesChatPair[]): CrossSpeciesChatPair[] {
  const normalized = new Map<string, CrossSpeciesChatPair>();
  for (const pair of pairs) {
    if (!isCrossSpeciesChatSpecies(pair.speciesA) || !isCrossSpeciesChatSpecies(pair.speciesB)) {
      throw new Error("Cross-species chat contains an unsupported species.");
    }
    if (pair.speciesA === pair.speciesB) {
      throw new Error("Cross-species chat pairs must contain two different species.");
    }
    const ordered = [pair.speciesA, pair.speciesB].sort((left, right) => left.localeCompare(right)) as [CrossSpeciesChatSpecies, CrossSpeciesChatSpecies];
    normalized.set(`${ordered[0]}:${ordered[1]}`, { speciesA: ordered[0], speciesB: ordered[1] });
  }
  return [...normalized.values()].sort((left, right) => `${left.speciesA}:${left.speciesB}`.localeCompare(`${right.speciesA}:${right.speciesB}`));
}

export function validateCrossSpeciesChatConfig(config: CrossSpeciesChatConfig): CrossSpeciesChatConfig {
  if (!Number.isInteger(config.maxRange) || config.maxRange < 100 || config.maxRange > 10_000) {
    throw new Error("Cross-species chat range must be a whole number from 100 to 10,000.");
  }
  if (config.allowedPairs.length > 100) {
    throw new Error("Cross-species chat supports at most 100 explicit species pairs.");
  }
  return { ...config, allowedPairs: normalizeCrossSpeciesChatPairs(config.allowedPairs) };
}
